import { isEqual } from 'lodash';

import { getCompanySourceByCode } from 'shared/companySource';

/**
 * Alternate FBM account name slugs → company source codes.
 * Prefer matching fbmAccountName directly to a company source code when possible.
 */
const FBM_ACCOUNT_TO_COMPANY_SOURCE_CODE = {
    blueapron: 'blue_apron',
    marleyspoon: 'marley_spoon',
};

/**
 * Resolves the partner's company source id from the selected FBM account.
 *
 * Lookup order:
 * 1. Treat normalized fbmAccountName as a company source code (e.g. blue_apron)
 * 2. Fall back to known alias slugs (e.g. blueapron → blue_apron)
 *
 * @param {Object|null|undefined} fbmAccount
 * @returns {number|null}
 */
export function resolvePartnerCompanySourceId(fbmAccount) {
    const accountName = fbmAccount?.fbmAccountName;
    if (!accountName) return null;

    const normalized = String(accountName).trim().toLowerCase();

    const directMatch = getCompanySourceByCode(normalized);
    if (directMatch) return directMatch.id;

    const aliasedCode = FBM_ACCOUNT_TO_COMPANY_SOURCE_CODE[normalized];
    return getCompanySourceByCode(aliasedCode)?.id ?? null;
}

/**
 * Keep prior page rows while paginating within the same partner account.
 * Drop placeholder data when companySourceIds change so account switches do not
 * briefly show the previous partner's purchase orders.
 *
 * Expects React Query keys shaped like `[..., params]` where `params.companySourceIds`
 * lives at index 4 (searchPaginated keys for grocery and packaging).
 *
 * @param {number[]|undefined} nextCompanySourceIds
 * @returns {(previousData: unknown, previousQuery: { queryKey?: unknown[] }) => unknown}
 */
export function createPartnerSearchPlaceholderData(nextCompanySourceIds) {
    return (previousData, previousQuery) => {
        const previousCompanySourceIds = previousQuery?.queryKey?.[4]?.companySourceIds;
        if (!isEqual(previousCompanySourceIds, nextCompanySourceIds)) {
            return undefined;
        }
        return previousData;
    };
}
